import React, { useState, useContext, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../App';
import { 
  Wrench, 
  ChevronRight, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  FileText,
  CheckCircle,
  ArrowRight,
  Shield,
  Clock,
  Loader2,
  ShoppingCart,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const MaterielDemande = () => {
  const { API } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    type_materiel: '',
    mode_acquisition: 'achat',
    code_postal: '',
    nom: '',
    telephone: '',
    email: '',
    besoin_details: '',
    rgpd_accepted: false
  });

  // Pre-fill type from URL param
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const objetParam = searchParams.get('objet');
    if (typeParam) {
      const validTypes = ['foreuse', 'sondeuse', 'pressiometre', 'injection', 'instrumentation', 'autre'];
      if (validTypes.includes(typeParam.toLowerCase())) {
        setFormData(prev => ({ ...prev, type_materiel: typeParam.toLowerCase() }));
      }
    }
    // Pre-fill details from objet param
    if (objetParam) {
      const objetLabels = {
        'etalonnage': 'Demande de devis pour étalonnage de capteurs',
        'location': 'Demande de location d\'instrumentation étalonnée',
        'contact': 'Demande de contact / rappel',
        'rappel': 'Demande de rappel téléphonique'
      };
      if (objetLabels[objetParam.toLowerCase()]) {
        setFormData(prev => ({ ...prev, besoin_details: objetLabels[objetParam.toLowerCase()] }));
      }
    }
  }, [searchParams]);

  const typesMateriels = [
    { value: 'foreuse', label: 'Foreuse géotechnique' },
    { value: 'sondeuse', label: 'Sondeuse' },
    { value: 'pressiometre', label: 'Pressiomètre' },
    { value: 'instrumentation', label: 'Instrumentation / Capteurs / Étalonnage' },
    { value: 'injection', label: 'Matériel d\'injection' },
    { value: 'autre', label: 'Autre matériel' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.type_materiel) {
      toast.error('Veuillez sélectionner un type de matériel');
      return;
    }
    if (!formData.code_postal.trim()) {
      toast.error('Le code postal est obligatoire');
      return;
    }
    if (!formData.nom.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!formData.telephone.trim()) {
      toast.error('Le téléphone est obligatoire');
      return;
    }
    if (!formData.rgpd_accepted) {
      toast.error('Veuillez accepter la politique de confidentialité');
      return;
    }

    setLoading(true);
    
    try {
      const form = new FormData();
      form.append('type_materiel', formData.type_materiel);
      form.append('mode_acquisition', formData.mode_acquisition);
      form.append('code_postal', formData.code_postal.trim());
      form.append('nom', formData.nom.trim());
      form.append('telephone', formData.telephone.trim());
      if (formData.email) form.append('email', formData.email.trim());
      if (formData.besoin_details) form.append('besoin_details', formData.besoin_details.trim());
      form.append('rgpd_accepted', formData.rgpd_accepted);

      await axios.post(`${API}/demandes/materiel`, form);
      
      setSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Demande envoyée | Matériel géotechnique | GEOTS Consulting</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#0A2540] mb-4">
                Demande envoyée !
              </h1>
              <p className="text-gray-600 mb-6">
                Votre demande a bien été envoyée. Nous vous recontactons rapidement avec des propositions adaptées.
              </p>
              <div className="flex flex-col gap-3">
                <Link 
                  to="/shop"
                  className="text-[#635BFF] hover:underline font-medium"
                >
                  Voir les annonces disponibles →
                </Link>
                <Link 
                  to="/"
                  className="text-gray-500 hover:text-gray-700"
                >
                  Retour à l'accueil
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Demande de matériel géotechnique | Devis achat & location | GEOTS</title>
        <meta name="description" content="Vous cherchez une foreuse, sondeuse ou matériel géotechnique ? Décrivez votre besoin et recevez des propositions de vendeurs et loueurs qualifiés." />
        <link rel="canonical" href="https://geots.consulting/materiel/demande" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/shop" className="hover:text-[#635BFF]">Boutique</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Demande de matériel</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="bg-[#635BFF]/20 p-3 rounded-xl">
                  <Wrench className="w-8 h-8 text-[#635BFF]" />
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Vous cherchez du matériel géotechnique ?
              </h1>
              <p className="text-gray-300 text-lg">
                Décrivez votre besoin en 2 minutes. Nous vous mettons en relation avec des vendeurs et loueurs qualifiés.
              </p>
            </div>
          </div>
        </header>

        {/* Form Section */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            
            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Shield className="w-5 h-5 text-green-500" />
                <span>100% gratuit</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-5 h-5 text-[#635BFF]" />
                <span>Réponse rapide</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-5 h-5 text-[#00D4FF]" />
                <span>Sans engagement</span>
              </div>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Material Type */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-[#635BFF]" />
                    Type de matériel recherché
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Matériel <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type_materiel"
                      value={formData.type_materiel}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all bg-white"
                      required
                    >
                      <option value="">Sélectionnez un type</option>
                      {typesMateriels.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Acquisition Mode */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-[#635BFF]" />
                    Mode d'acquisition
                  </h2>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <label 
                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.mode_acquisition === 'achat' 
                          ? 'border-[#635BFF] bg-[#635BFF]/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="mode_acquisition"
                        value="achat"
                        checked={formData.mode_acquisition === 'achat'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <ShoppingCart className={`w-6 h-6 ${formData.mode_acquisition === 'achat' ? 'text-[#635BFF]' : 'text-gray-400'}`} />
                      <div>
                        <p className={`font-semibold ${formData.mode_acquisition === 'achat' ? 'text-[#635BFF]' : 'text-gray-700'}`}>
                          Achat
                        </p>
                        <p className="text-xs text-gray-500">Neuf ou occasion</p>
                      </div>
                    </label>
                    
                    <label 
                      className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.mode_acquisition === 'location' 
                          ? 'border-[#00D4FF] bg-[#00D4FF]/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="mode_acquisition"
                        value="location"
                        checked={formData.mode_acquisition === 'location'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <RotateCcw className={`w-6 h-6 ${formData.mode_acquisition === 'location' ? 'text-[#00D4FF]' : 'text-gray-400'}`} />
                      <div>
                        <p className={`font-semibold ${formData.mode_acquisition === 'location' ? 'text-[#00D4FF]' : 'text-gray-700'}`}>
                          Location
                        </p>
                        <p className="text-xs text-gray-500">Courte ou longue durée</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#635BFF]" />
                    Localisation
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal / Département <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="code_postal"
                      value={formData.code_postal}
                      onChange={handleChange}
                      placeholder="Ex: 75001 ou 75"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <User className="w-5 h-5 text-[#635BFF]" />
                    Vos coordonnées
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleChange}
                          placeholder="06 12 34 56 78"
                          className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre@email.com"
                          className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Précisez votre besoin
                  </h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (facultatif)
                    </label>
                    <textarea
                      name="besoin_details"
                      value={formData.besoin_details}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Marque, modèle, capacité, budget, durée de location, contraintes particulières..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent transition-all resize-none"
                    />
                  </div>
                </div>

                {/* RGPD */}
                <div className="border-t pt-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="rgpd_accepted"
                      checked={formData.rgpd_accepted}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[#635BFF] focus:ring-[#635BFF]"
                      required
                    />
                    <span className="text-sm text-gray-600">
                      J'accepte que mes données soient traitées par GEOTS Consulting pour répondre à ma demande, 
                      conformément à la <Link to="/privacy-policy" className="text-[#635BFF] hover:underline">politique de confidentialité</Link>. 
                      <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#635BFF] to-[#7C3AED] hover:from-[#5248e5] hover:to-[#6d28d9] text-white font-semibold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Envoyer ma demande
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Bottom info */}
            <p className="text-center text-sm text-gray-500 mt-6">
              Vos données sont protégées et ne seront jamais revendues. 
              Aucun compte n'est créé automatiquement.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaterielDemande;
